// Project-canvas left rail actions (ProjectCanvasSidebar) — filter the canvas
// items, switch the active environment, and open the project live / preview.
// Modeled on the /infra sidebar's infraSetFilter / infraSwitchEnv helpers.
// (2026-06-17 per Nika "similar sidebar as we have on /infra on project canvas
// page, where we can filter stuff, switch environments, publish button etc".)

// Bare shared-library import, not per-file deep functions/*.js subpaths — a
// per-file deep specifier into @symbo-ls/workspace-shared cannot resolve
// through the platform's public importmap (tickets/execution-board.md §1,
// same fix as workspace/app.js). Deferred call wrappers, not a module-scope
// destructure, per the a5d0a2aa barrel-cycle lesson.
import _wsShared from '@symbo-ls/workspace-shared'
const buildProjectUrls = (...args) => _wsShared.functions.buildProjectUrls(...args)

const _canvasRoot = (el) => el?.state?.root || el?.getRoot?.()?.state?.root || null

// owner/key from the /:owner/:key route (the canvas project path).
const _canvasOwnerKey = () => {
  const segs = (typeof window !== 'undefined' ? window.location.pathname : '')
    .split('/')
    .filter(Boolean)
  return { owner: segs[0], key: segs[1] }
}

// Canvas item filter: 'all' | 'pages' | 'components'. The CanvasSurface CvItems
// children factory reads `_canvasFilter` (subscribeTo) and filters page vs
// component keys against root.data.
export function setCanvasFilter(filter) {
  const root = _canvasRoot(this)
  if (!root) return
  root.update({ _canvasFilter: filter || 'all' })
  // Re-draw screen-space chrome (item titles, selection box) so it hides for the
  // now-filtered-out items. CvItems re-renders via its own subscribeTo.
  if (this.call) this.call('refreshCanvasChrome')
}

// Active environment: 'production' | 'staging' | 'development'. Display state for
// now (drives the picker label + which live URL Publish/Preview opens).
export function switchCanvasEnv(env) {
  const root = _canvasRoot(this)
  if (!root) return
  root.update({ _canvasEnv: env || 'production' })
}

// Open the project's LIVE deployed URL for the active env (the canonical
// `owner--key[--env].at.symbo.ls`, same builder as /studio "Open directly").
export function openProjectLive() {
  const root = _canvasRoot(this)
  const { owner, key } = _canvasOwnerKey()
  if (!owner || !key) return
  const env = root?._canvasEnv || 'production'
  const urls = buildProjectUrls(owner, key, env)
  const url =
    env === 'staging'
      ? urls?.directStaging
      : env === 'development'
        ? urls?.directDev
        : urls?.directProd
  if (url && typeof window !== 'undefined') window.open(url, '_blank', 'noopener')
}

// Open the project preview in the shell CanvasPageSlot (the /preview viewer) —
// the same floating pane /studio uses when a project is selected. Toggles, like
// the /infra "Status" item: clicking again closes it. (2026-06-17 per Nika
// "use preview project in PageSlot just like we do it on /studio sidebar").
export function openProjectPreviewPane() {
  const root = _canvasRoot(this)
  if (!root) return
  const cur = (root._canvasSlot || '').split('?')[0]
  root.update({ _canvasSlot: cur === '/preview' ? null : '/preview' })
}

// Open the project PREVIEW for the active env (the preview host) in a new tab.
export function openProjectPreview() {
  const root = _canvasRoot(this)
  const { owner, key } = _canvasOwnerKey()
  if (!owner || !key) return
  const env = root?._canvasEnv || 'production'
  const urls = buildProjectUrls(owner, key, env)
  if (urls?.preview && typeof window !== 'undefined')
    window.open(urls.preview, '_blank', 'noopener')
}

// Hide or show the calling element's page sidebar — the voice command "toggle
// the sidebars" (functions/voiceCommands.js). The page-sidebar rule (Nika
// 2026-10-29, "Same rule for all"): one per-page choice, kept with the look
// (shared functions/pageSidebar.js), so this is the shared toggle and no
// session flag of its own. Where the host cannot remember (no look), nothing
// happens: the sidebar always shows there.
export function toggleSidebars() {
  const fns = (this && this.context && this.context.functions) || {}
  if (typeof fns.pageSidebarToggle !== 'function') return null
  return this.call('pageSidebarToggle')
}

// ── PAGES group: the board pages AND the project's own pages ───────────────
// Nika 2026-10-31 — "Yes, separate lane (Recommended)" to "Must the PAGES group
// also show the project pages?" — after W4 on dev (2026-10-30): the AI added a
// page to Velvet Lantern, its tile landed on the board, and PAGES still listed
// only "Page 1". The group lists the Figma-style board pages (Nika 2026-08-01)
// and, under each, the project pages (the site routes in `root.data.pages`)
// whose tiles that board page holds.
//
// A project page's tile is the board item whose KEY is the route — the one
// identity syncCanvasItemsFromPages, the "Pages" filter and the on-board tile
// titles already use (a duplicate is a different key and lists nowhere). Key
// tests use `in`, as those readers do: `root.data` is a state store, and `in`
// is the membership test its contract names.

// The board page that holds a project page's tile: the ACTIVE page's live
// `items` first (its `pageItems` snapshot is stale while it is open), then each
// parked page in board order. '' = the tile is on no board page.
const _projectPageHolderId = (v2, route) => {
  const boardPages = Array.isArray(v2.pages) ? v2.pages : []
  const active = boardPages.some((p) => p.id === v2.activePageId) ? v2.activePageId : ''
  if (active && v2.items && route in v2.items) return active
  const parked = v2.pageItems || {}
  for (const p of boardPages) {
    if (p.id === active) continue
    const items = parked[p.id] && parked[p.id].items
    if (items && route in items) return p.id
  }
  return ''
}

// el.call('canvasPagesGroupRows') → the PAGES group rows: each board page in
// board order, followed by the project pages its board holds (in the project's
// own page order), then the project pages on no board page. The label of a
// project page row is its route — the same text as its tile's title on the
// board, a DATA value, never a bundle key.
//
// Every row carries the SAME keys: a key a `childrenAs: 'state'` row does not
// hold reads through to ROOT state. Every row also carries a stable `key`, so
// the keyed reconcile keeps each row's node: a page that lands mid-rename never
// moves the rename input onto another row.
export function canvasPagesGroupRows() {
  const v2 = (this.call && this.call('getCanvasV2')) || {}
  const boardPages = Array.isArray(v2.pages) ? v2.pages : []
  // No board page yet = the board is not initialised (a board always keeps one
  // page once it is): there is no board to hold or show a tile, so list nothing.
  if (!boardPages.length) return []
  const active = v2.activePageId
  const primary = (v2.selection && v2.selection.primary) || null
  const root = _canvasRoot(this)
  const routes = Object.keys((root && root.data && root.data.pages) || {})
  const held = {}
  for (const route of routes) {
    const holder = _projectPageHolderId(v2, route)
    if (!held[holder]) held[holder] = []
    held[holder].push(route)
  }
  // A project page row highlights while its tile is the primary selection,
  // which only the open board page has.
  const projectPageRow = (route, holder) => ({
    key: 'route:' + route,
    id: 'route:' + route,
    name: route,
    _active: !!holder && holder === active && route === primary,
    _isAdd: false,
    _route: route,
    _nested: !!holder
  })
  const rows = []
  for (const p of boardPages) {
    rows.push({
      key: p.id,
      id: p.id,
      name: p.name || '',
      _active: p.id === active,
      _isAdd: false,
      _route: '',
      _nested: false
    })
    for (const route of held[p.id] || []) rows.push(projectPageRow(route, p.id))
  }
  for (const route of held[''] || []) rows.push(projectPageRow(route, ''))
  return rows
}

// el.call('focusProjectPage', route) — a project page row's click in the PAGES
// group: show that page's tile on the board, the same "go to it" the ITEMS
// rows give an entity node.
//   1. Open the board page that holds the tile (switchBoardPage). A tile on no
//      board page gets placed on the OPEN board page by the canonical reconcile
//      (syncCanvasItemsFromPages — the same pass every AI write runs).
//   2. A page tile shows only under the "All" and "Pages" filters (the
//      CanvasSurface CvItems `hide`), so any other filter lifts to "All".
//   3. Centre + select the tile at the live zoom (focusCanvasEntity).
// Returns whether a tile got the focus. Store writes land at once, so each
// step reads the board the step before it left.
export function focusProjectPage(route) {
  const root = _canvasRoot(this)
  const pages = root && root.data && root.data.pages
  if (!route || !pages || !(route in pages)) return false
  const v2 = this.call('getCanvasV2')
  if (!v2) return false
  const holder = _projectPageHolderId(v2, route)
  if (!holder) this.call('syncCanvasItemsFromPages')
  else if (holder !== v2.activePageId) this.call('switchBoardPage', holder)
  const filter = root._canvasFilter || 'all'
  if (filter !== 'all' && filter !== 'pages') this.call('setCanvasFilter', 'all')
  return this.call('focusCanvasEntity', route) === true
}

// ── Selection-aware AI prompting (2026-08-08 per Nika "ai should understand
// selected artboards") ──
//
// buildSelectionContext(v2, data) — PURE, side-effect-free. Resolves the
// canvas v2 selection (`v2.selection.ids`) against a `{ pages, components }`
// map into the shape the shell composer's send threads through (a compact
// "Selected artboards: …" text prefix + the structured `attached` payload —
// workspace app.js CanvasPromptTextarea → homeAiSend). Returns the exact
// byte-identical OFF-state (`{ selectedItems: [], promptPrefix: '' }`) when
// there's no v2 or nothing selected — every call site below relies on that
// to keep the no-selection path untouched.
//
// `title` mirrors the ONE existing "item title" convention in this codebase
// (refreshCanvasChromeNow's CvItemTitles labels, functions/canvas/state.js):
// a real page/component key has no separate title field anywhere in the
// project schema (page/component defs are bare DOMQL objects), so the key
// IS the title — except the built-in `__ds/<route>` design-system frames,
// which get the same friendly "Design system · Colors" label those on-canvas
// titles already use. `type` is deliberately the 'page'|'component' union
// the ticket asks for (not a 3rd 'ds' bucket) — a selected design-system tile
// falls back to 'component', the closest bucket, same default direction
// CanvasSurface's own `cvType` resolution uses for "other" keys.
const _DS_PREFIX = '__ds/'
const _ENTITY_PREFIX = 'ref:'
// Caps from spec §3.8. A board can hold hundreds of nodes; a turn prompt
// cannot. Truncation is deterministic (board order, then the connections whose
// BOTH endpoints survived) so the same board always attaches the same slice.
const _ATTACH_MAX_NODES = 60
const _ATTACH_MAX_CONNECTIONS = 120

// The ATTACHED canvas payload (spec §3.8) — PURE, built from the same
// (v2, data) pair as the selection below.
//
// Scope: the SELECTED entity items when the selection contains any, else the
// WHOLE active board. Returns null when the board carries no entity node at
// all, which is what keeps both results below byte-identical for every
// page/component board — i.e. every board that existed before entity nodes.
const _attachedCanvas = (v2, data, ids) => {
  const items = (v2 && v2.items) || {}
  const entities = (data && data.entities) || (v2 && v2.entities) || {}
  const isEntity = (k) => typeof k === 'string' && k.indexOf(_ENTITY_PREFIX) === 0
  const selected = (ids || []).filter(isEntity)
  const pool = selected.length ? selected : Object.keys(items).filter(isEntity)
  if (!pool.length) return null
  const keys = pool.slice(0, _ATTACH_MAX_NODES)
  const inSet = new Set(keys)
  // The ref string IS the key. The fields come from the resolve cache when a
  // card has already loaded that node and degrade to the bare ref when it has
  // not — a half-resolved board must still attach something usable.
  const nodes = keys.map((key) => {
    const d = (entities[key] && entities[key].data) || {}
    return {
      key,
      ref: key,
      title: d.title || key,
      kind: d.kind || '',
      status: d.status || '',
      effectiveDate: d.effectiveDate || '',
      summary: d.summary || ''
    }
  })
  const connections = []
  for (const c of (v2 && v2.connections) || []) {
    if (connections.length >= _ATTACH_MAX_CONNECTIONS) break
    if (!inSet.has(c.from) || !inSet.has(c.to)) continue
    connections.push({ from: c.from, to: c.to, kind: c.kind || '', label: c.label || '' })
  }
  const boardPageId = (v2 && v2.activePageId) || ''
  const page = ((v2 && Array.isArray(v2.pages) && v2.pages) || []).find((p) => p.id === boardPageId)
  return {
    canvas: {
      projectId: (data && data.projectId) || '',
      boardPageId,
      title: (page && page.name) || '',
      nodes,
      connections
    }
  }
}

export function buildSelectionContext(v2, data) {
  const ids = (v2 && v2.selection && Array.isArray(v2.selection.ids) && v2.selection.ids) || []
  // `attached` is ADDITIVE and only ever present when the board actually holds
  // entity nodes — so both the no-selection off-state and the page/component
  // selection result stay byte-identical to what every existing call site (and
  // __tests__/buildSelectionContext.test.js) asserts.
  const attached = _attachedCanvas(v2, data, ids)
  if (!ids.length) {
    return attached
      ? { selectedItems: [], promptPrefix: '', attached }
      : { selectedItems: [], promptPrefix: '' }
  }
  const pages = (data && data.pages) || {}
  const components = (data && data.components) || {}
  const selectedItems = ids.map((key) => {
    const isDs = typeof key === 'string' && key.indexOf(_DS_PREFIX) === 0
    const type = key in pages ? 'page' : 'component'
    const title = isDs
      ? 'Design system · ' + key.slice(_DS_PREFIX.length).replace(/^\w/, (c) => c.toUpperCase())
      : key
    return { key, type, title }
  })
  const label = selectedItems.map((it) => it.title).join(', ')
  const promptPrefix =
    `Selected artboards: ${label} — apply the request to these unless stated otherwise` + '\n\n'
  return attached ? { selectedItems, promptPrefix, attached } : { selectedItems, promptPrefix }
}

// el.call('getSelectionAiContext') — the `this`-bound wrapper buildSelectionContext
// callers actually invoke. Resolves the LIVE v2 selection against root.data and
// returns buildSelectionContext's result. Registered in the canvas EAGER
// function slice (functions/eagerSlice.js re-exports this whole file), so it's
// callable from the shell-root CanvasPromptTextarea mount (workspace/app.js) on
// every route, not just canvas-manifest ones — safe there too: with no v2 (a
// non-canvas route) buildSelectionContext gets `v2: null` and returns the same
// empty off-state.
export function getSelectionAiContext() {
  const root = _canvasRoot(this)
  if (!root) return { selectedItems: [], promptPrefix: '' }
  const v2 = this.call ? this.call('getCanvasV2') : null
  const pages = root.data?.pages || {}
  const components = root.data?.components || {}
  // `projectId` + `entities` feed the ADDITIVE `attached.canvas` payload
  // (spec §3.8). Both are optional in buildSelectionContext, so the pure
  // function keeps working for every existing caller that passes only
  // `{ pages, components }`.
  return buildSelectionContext(v2, {
    pages,
    components,
    projectId: root.data?._id || root.data?.id || root.projectId || '',
    entities: (v2 && v2.entities) || {}
  })
}
